import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { FetchDataArg } from '../utils/fetch';
import { RootState } from '../store';

const getParsedCuidList = (data: CuidList) =>
  data.reduce((prev, cur) => {
    const { cuid, siteId, siteName, ...rest } = cur;
    const site = { siteId, siteName };
    if (prev[cuid]) {
      prev[cuid].sites = [...prev[cuid].sites, site];
    } else {
      prev[cuid] = { cuid, ...rest, sites: [site] };
    }

    return prev;
  }, {} as ParsedCuidList);

export const fetchData = createAsyncThunk(
  'cuidList/fetchData',
  async (arg: FetchDataArg, { getState }) => {
    const state = getState() as RootState;
    const lastFetchTime = state.cuidList.lastFetchTime;
    const cacheDuration = 5 * 60 * 1000;

    if (
      state.cuidList.data &&
      lastFetchTime &&
      Date.now() - lastFetchTime < cacheDuration
    ) {
      // if have cached data using it
      return state.cuidList.data as CuidListSliceType;
    }

    const response = await arg.fetch<{ data: CuidList }>(
      arg.endpoint,
      arg.options
    );

    const { status } = response;
    if (status === 'error') return response;
    else
      return {
        ...response,
        data: getParsedCuidList(response.data),
      } as CuidListSliceType;
  }
);

type CuidListSliceType = GridusResponseType<{ data: ParsedCuidList }>;

const initialState: SliceInitialState<CuidListSliceType> = {
  data: null,
  loading: false,
  error: null,
  lastFetchTime: null,
};

const cuidListSlice = createSlice({
  name: 'cuidList',
  initialState,
  reducers: {
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchData.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchData.fulfilled,
        (
          state,
          action: {
            payload: CuidListSliceType | GridusResponseErrorType;
          }
        ) => {
          state.loading = false;
          state.lastFetchTime = Date.now();
          if (action.payload.status === 'success') {
            state.error = null;
            state.data = action.payload as CuidListSliceType;
          } else {
            state.data = null;
            state.error = action.payload as GridusResponseErrorType;
          }
        }
      )
      .addCase(fetchData.rejected, (state, action) => {
        state.lastFetchTime = Date.now();
        state.loading = false;
        state.data = null;
        state.error = action.error as GridusResponseErrorType;
      });
  },
});

export const { resetState: cuidListResetState } = cuidListSlice.actions;
export default cuidListSlice.reducer;
